/**
 * `<AutoroutedBadge>` — the small "Autorouted" pill shown next to a note title on every
 * title surface (notebooks tree, editor doc-head, search results, corpus snapshot).
 *
 * Rendered only when the note id is in the cached routed-set (`useRoutedNoteIdsQuery`).
 *
 * It explains the Autorouter two ways (AUTOROUTER-SPEC.md §3.1):
 *   - **on hover** — a native tooltip with the one-line "what is this" explanation;
 *   - **on click** — a popover with this note's actual routing decision (which folder, the
 *     confidence + reason, any rename/tags, when), plus "Send back to Inbox" and a jump to
 *     Settings → Autorouter.
 *
 * The popover is portalled to <body> and fixed-positioned from the badge's rect so it is never
 * clipped by the scrolling notebooks pane. The badge persists until a human or MCP moves the
 * note (the backend supersedes the routing-log row, dropping the id from the set).
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  useAutorouterActivityQuery,
  useRoutedNoteIdsQuery,
  useUndoAutoroutingMutation,
} from '@/hooks';
import { Icon } from './Icon';

const HOVER_TOOLTIP =
  'Autorouted — Aletheia filed this note automatically from your Inbox using your routing ' +
  'prompt. Click for details.';

const POPOVER_WIDTH = 300;

export function AutoroutedBadge({ noteId }: { noteId: string }): JSX.Element | null {
  const { data: routed } = useRoutedNoteIdsQuery();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    // A fixed-positioned popover detaches from the badge if the page scrolls — close it.
    const onScrollOrResize = (): void => setOpen(false);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open]);

  if (!routed || !routed.has(noteId)) return null;

  const toggle = (e: React.MouseEvent): void => {
    // Never let the click reach the row underneath (which would open / select the note).
    e.stopPropagation();
    e.preventDefault();
    if (open) {
      setOpen(false);
      return;
    }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const left = Math.max(8, Math.min(r.left, window.innerWidth - POPOVER_WIDTH - 12));
      setCoords({ top: r.bottom + 6, left });
    }
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="autorouted-badge"
        title={HOVER_TOOLTIP}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Autorouted — click for details"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={toggle}
      >
        <Icon name="route" size={11} />
        <span className="autorouted-badge__label">Autorouted</span>
        <Icon name="info" size={10} className="autorouted-badge__info" />
      </button>
      {open &&
        coords &&
        createPortal(
          <AutoroutedPopover
            noteId={noteId}
            coords={coords}
            popRef={popRef}
            onClose={() => setOpen(false)}
          />,
          document.body,
        )}
    </>
  );
}

interface PopoverProps {
  noteId: string;
  coords: { top: number; left: number };
  popRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
}

function AutoroutedPopover({ noteId, coords, popRef, onClose }: PopoverProps): JSX.Element {
  const navigate = useNavigate();
  // Lazily fetched — this query only fires once a badge is actually clicked. All badges share
  // the cache key, so it's one request regardless of how many badges are on screen.
  const { data: activity } = useAutorouterActivityQuery(50);
  const undo = useUndoAutoroutingMutation();
  const entry = activity?.find((a) => a.noteId === noteId && a.undoneAt === null) ?? null;

  return (
    <div
      ref={popRef}
      className="autorouted-pop"
      role="dialog"
      aria-label="Autorouter details"
      style={{ position: 'fixed', top: coords.top, left: coords.left, width: POPOVER_WIDTH }}
    >
      <div className="autorouted-pop__head">
        <Icon name="route" size={13} />
        <strong>Autorouted</strong>
      </div>
      <p className="autorouted-pop__lede">
        Aletheia filed this note automatically from your Inbox using your routing prompt — no
        manual sorting. The badge stays until you (or an integration) move the note.
      </p>

      {entry ? (
        <dl className="autorouted-pop__detail">
          <DetailRow label="Filed into" value={entry.toNotebookTitle ?? '—'} />
          {entry.renamedTo !== null && <DetailRow label="Renamed to" value={entry.renamedTo} />}
          <DetailRow label="Confidence" value={entry.confidence} />
          {entry.tagsApplied.length > 0 && (
            <DetailRow label="Tags" value={entry.tagsApplied.join(', ')} />
          )}
          {entry.reason && <div className="autorouted-pop__reason">“{entry.reason}”</div>}
          <div className="autorouted-pop__when">routed {formatWhen(entry.routedAt)}</div>
        </dl>
      ) : (
        <p className="autorouted-pop__muted">
          The specific routing details for this note aren’t in the recent activity log.
        </p>
      )}

      <div className="autorouted-pop__actions">
        {entry && entry.supersededAt === null && (
          <button
            type="button"
            className="btn btn--sm"
            disabled={undo.isPending}
            title="Move this note back to the Inbox and clear the badge"
            onClick={() => {
              undo.mutate(entry.id);
              onClose();
            }}
          >
            Send back to Inbox
          </button>
        )}
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => {
            onClose();
            navigate('/settings?section=autorouter');
          }}
        >
          Autorouter settings
        </button>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="autorouted-pop__row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** Compact relative time — "just now" / "12m ago" / "3h ago" / "5d ago" / a date past 30 days. */
function formatWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
